import { useState, useEffect } from 'react';
import { formatPhone } from '../components/formatPhone';
import { auth } from '../firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhone, FaHome, FaLock } from 'react-icons/fa';
import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import '../PropertyManager/Settings.css';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

function TenantSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [tenantPhone, setTenantPhone] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  // Edit profile state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({ Full_Name: '', Phone_Number: '', Email: '' });
  const [editMsg, setEditMsg] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const res = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const data = await res.json();
        setSession(data);
        const doorId = data?.tenant?.door_id;
        if (doorId) {
          try {
            const leaseRes = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${doorId}`);
            const leaseData = await leaseRes.json();
            const phone = leaseData?.tenants?.[0]?.Phone_Number || '';
            setTenantPhone(phone);
          } catch {}
        }
      } catch {}
      setLoading(false);
    });
    return unsub;
  }, [navigate]);

  const handleEditOpen = () => {
    const t = session?.tenant;
    setEditForm({ Full_Name: t?.full_name || '', Phone_Number: tenantPhone, Email: t?.email || '' });
    setEditMsg('');
    setShowEditProfile(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setEditMsg('');
    setEditSaving(true);
    try {
      const tenantId = session?.tenant?.tenant_id;
      const params = new URLSearchParams({
        Full_Name: editForm.Full_Name,
        Phone_Number: editForm.Phone_Number,
        Email: editForm.Email,
        tenant_id: tenantId,
      });
      const res = await fetch(`${BASE_URL}/TenantApp/UpdateTenantInfo?${params}`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      setSession(prev => ({ ...prev, tenant: { ...prev.tenant, full_name: editForm.Full_Name, email: editForm.Email } }));
      setTenantPhone(editForm.Phone_Number);
      setShowEditProfile(false);
    } catch {
      setEditMsg('Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    if (newPassword !== confirmPassword) { setPasswordMsg('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setPasswordMsg('Password must be at least 6 characters.'); return; }
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordMsg('Password updated successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setShowPasswordForm(false);
    } catch {
      setPasswordMsg('Current password is incorrect.');
    }
  };

  if (loading) return <LoadingScreen />;

  const tenant = session?.tenant;
  const door = session?.door;
  const displayName = tenant?.full_name || '';
  const initials = displayName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
  const address = door ? [door.street_address, door.unit_number ? `Unit ${door.unit_number}` : '', `${door.city}, ${door.state} ${door.zipcode}`].filter(Boolean).join(' · ') : '—';

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar title="Settings" tenantName={tenant?.full_name || ''} initials={initials} />

        <div className="settings-container">

          {/* Profile */}
          <div className="settings-card settings-profile-card">
            <div className="settings-profile-header">
              <div className="settings-avatar">{initials}</div>
              <div className="settings-profile-info">
                <h2 className="settings-profile-name">{tenant?.full_name || '—'}</h2>
                <p className="settings-profile-email">{tenant?.email || ''}</p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="settings-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="settings-card-title" style={{ margin: 0 }}>Account Details</h3>
              {!showEditProfile && (
                <button className="settings-btn settings-btn-outline" onClick={handleEditOpen}>Edit</button>
              )}
            </div>

            {showEditProfile ? (
              <form className="settings-password-form" onSubmit={handleSaveProfile}>
                <label className="settings-detail-label" style={{ marginBottom: 4 }}>Full Name</label>
                <input className="settings-input" value={editForm.Full_Name} onChange={e => setEditForm(f => ({ ...f, Full_Name: e.target.value }))} required />
                <label className="settings-detail-label" style={{ marginBottom: 4, marginTop: 8 }}>Phone Number</label>
                <input className="settings-input" value={editForm.Phone_Number} onChange={e => setEditForm(f => ({ ...f, Phone_Number: formatPhone(e.target.value) }))} />
                <label className="settings-detail-label" style={{ marginBottom: 4, marginTop: 8 }}>Email</label>
                <input className="settings-input" type="email" value={editForm.Email} disabled style={{ opacity: 0.5 }} />
                {editMsg && <p className="settings-msg error">{editMsg}</p>}
                <div className="settings-delete-actions" style={{ marginTop: 12 }}>
                  <button type="submit" className="settings-btn settings-btn-primary" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
                  <button type="button" className="settings-btn settings-btn-outline" onClick={() => setShowEditProfile(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className="settings-details-grid">
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaUser /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Full Name</span>
                    <span className="settings-detail-value">{tenant?.full_name || '—'}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaEnvelope /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Email</span>
                    <span className="settings-detail-value">{tenant?.email || '—'}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaHome /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Property</span>
                    <span className="settings-detail-value">{address}</span>
                  </div>
                </div>
                <div className="settings-detail-item">
                  <div className="settings-detail-icon"><FaPhone /></div>
                  <div className="settings-detail-content">
                    <span className="settings-detail-label">Phone Number</span>
                    <span className="settings-detail-value">{formatPhone(tenantPhone) || '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Security */}
          <div className="settings-card">
            <h3 className="settings-card-title">Security</h3>
            <div className="settings-security-row">
              <div className="settings-security-info">
                <FaLock className="settings-security-icon" />
                <div>
                  <span className="settings-detail-label">Password</span>
                  <span className="settings-detail-value">••••••••</span>
                </div>
              </div>
              <button className="settings-btn settings-btn-outline" onClick={() => { setShowPasswordForm(!showPasswordForm); setPasswordMsg(''); }}>
                Change Password
              </button>
            </div>
            {showPasswordForm && (
              <form className="settings-password-form" onSubmit={handleChangePassword}>
                <input type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="settings-input" required />
                <input type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="settings-input" required />
                <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="settings-input" required />
                <button type="submit" className="settings-btn settings-btn-primary">Update Password</button>
                {passwordMsg && <p className={`settings-msg ${passwordMsg.includes('success') ? 'success' : 'error'}`}>{passwordMsg}</p>}
              </form>
            )}
          </div>

          {/* Guided Tours */}
          <div className="settings-card">
            <h3 className="settings-card-title">Guided Tours</h3>
            <p className="settings-danger-text" style={{ color: '#6b7280', marginBottom: 14 }}>
              Reset the tutorial for any page so it plays again the next time you visit.
            </p>
            <div className="settings-tours-grid">
              {[
                { label: 'Home',            key: 'hasSeenTenantHomeTutorial' },
                { label: 'Documents',       key: 'hasSeenTenantDocsTutorial' },
                { label: 'Maintenance',     key: 'hasSeenTenantMaintenanceTutorial' },
                { label: 'Payments',        key: 'hasSeenTenantPaymentsTutorial' },
                { label: 'Payment History', key: 'hasSeenPayHistoryTutorial' },
              ].map(({ label, key }) => (
                <button
                  key={key}
                  className="settings-btn settings-btn-outline"
                  onClick={() => {
                    const email = tenant?.email || '';
                    if (email) {
                      localStorage.removeItem(`${key}_${email}`);
                      showToast(`${label} tutorial will play on your next visit to that page.`, 'info');
                    }
                  }}
                >
                  Replay {label} Tutorial
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default TenantSettings;
